import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  InputField,
  SubmitButton,
} from '@saas-kit/ui';
import { DataTable, type DataTableColumn } from '@saas-kit/ui/DataTable';

import { useQmsCrud } from './useQmsCrud';

interface SimpleRow {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

type SimpleCatalogKind = 'category' | 'counter_group';

interface Props<T extends SimpleRow> {
  cacheKey: string;
  kind: SimpleCatalogKind;
  canManage: boolean;
  enabled: boolean;
  list: () => Promise<T[]>;
  create: (input: { code: string; name: string }) => Promise<T>;
  update: (id: string, input: Record<string, unknown>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

/** Code + name + active CRUD, shared by the Categories and Counter Groups tabs. */
export function SimpleCatalogTab<T extends SimpleRow>(
  props: Props<T>,
): JSX.Element {
  const { t } = useTranslation('qms');
  const codePlaceholder = t(`placeholders.${props.kind}_code`);
  const namePlaceholder = t(`placeholders.${props.kind}_name`);
  const crud = useQmsCrud<T, { code: string; name: string }>({
    key: props.cacheKey,
    list: props.list,
    create: props.create,
    update: props.update,
    remove: props.remove,
    enabled: props.enabled,
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    await crud.create({ code: code.trim(), name: name.trim() });
    setCode('');
    setName('');
  };

  const columns: DataTableColumn<T>[] = [
    { id: 'code', header: t('fields.code'), cell: (r) => r.code },
    { id: 'name', header: t('fields.name'), cell: (r) => r.name },
    {
      id: 'status',
      header: t('fields.status'),
      cell: (r) =>
        r.is_active ? t('status.active') : t('status.inactive'),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      exportSkip: true,
      cell: (r) =>
        props.canManage ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() =>
                crud.update(r.id, { is_active: !r.is_active })
              }
            >
              {r.is_active ? t('actions.deactivate') : t('actions.activate')}
            </Button>
            <Button variant="ghost" onClick={() => setPendingDelete(r.id)}>
              {t('actions.delete')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {props.canManage ? (
        <Card>
          <CardContent className="p-5">
            <form
              onSubmit={(e) => void submit(e)}
              className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-end"
            >
              <InputField
                label={t('fields.code')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={codePlaceholder}
                required
              />
              <InputField
                label={t('fields.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={namePlaceholder}
                required
              />
              <SubmitButton
                loading={crud.creating}
                label={t('actions.create')}
                loadingLabel={t('actions.create')}
              />
            </form>
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        data={crud.items}
        columns={columns}
        rowKey={(r) => r.id}
        selectable={props.canManage}
        isLoading={crud.isLoading}
        error={crud.isError ? t('errors.view_denied') : null}
        emptyText={t('empty')}
        bulkActions={
          props.canManage
            ? [
                {
                  id: 'delete',
                  label: t('actions.delete'),
                  variant: 'destructive',
                  onRun: (rows) =>
                    crud.removeMany(rows.map((row) => row.id)),
                },
              ]
            : []
        }
      />

      {pendingDelete ? (
        <ConfirmDialog
          title={t('confirm.delete_title')}
          body={t('confirm.delete_body')}
          confirmLabel={t('confirm.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={crud.removing}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            crud.remove(pendingDelete);
            setPendingDelete(null);
          }}
        />
      ) : null}
    </div>
  );
}
